<template>
  <div class="ski-log-detail-container">
    <LoadingSpinner v-if="loading" />
    <template v-else>
      <!-- Header -->
      <div class="header">
        <button @click="goBack" class="back-button">
          ← Back to Logs
        </button>
        <div class="header-actions">
          <button @click="deleteLog" class="action-button danger">
            Delete
          </button>
          <button @click="saveLog" class="action-button primary" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>

      <!-- Log Details Form -->
      <div class="log-form-card">
        <h2>Edit Ski Session</h2>
        
        <div class="form-grid">
          <div class="form-group">
            <label for="date">Date</label>
            <input
              type="date"
              id="date"
              v-model="log.date"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="lake">Lake/Location</label>
            <input
              type="text"
              id="lake"
              v-model="log.lake"
              placeholder="Lake name or location"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="boat">Boat</label>
            <select id="boat" v-model="log.boat" class="form-input">
              <option value="">Select boat...</option>
              <option v-for="(label, value) in boatChoices" :key="value" :value="value">
                {{ label }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="boat_type">Boat Model</label>
            <input
              type="text"
              id="boat_type"
              v-model="log.boat_type"
              placeholder="e.g., 2023 G23"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="ski">Ski</label>
            <select id="ski" v-model="log.ski" class="form-input">
              <option value="">Select ski...</option>
              <option v-for="(label, value) in skiChoices" :key="value" :value="value">
                {{ label }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="ski_type">Ski Model</label>
            <input
              type="text"
              id="ski_type"
              v-model="log.ski_type"
              placeholder="e.g., Radar Senate"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="driver">Driver</label>
            <input
              type="text"
              id="driver"
              v-model="log.driver"
              placeholder="Who was driving"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="conditions">Conditions (%)</label>
            <input
              type="number"
              id="conditions"
              v-model="log.conditions"
              min="0"
              max="100"
              placeholder="0-100"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="conditions_detail">Conditions Details</label>
            <textarea
              id="conditions_detail"
              v-model="log.conditions_detail"
              placeholder="Additional details about water conditions..."
              class="form-textarea"
              rows="2"
            ></textarea>
          </div>
        </div>
      </div>

      <!-- Passes Table -->
      <div class="passes-section">
        <div class="section-header">
          <h3>Passes</h3>
          <button @click="addPass" class="action-button primary small">
            + Add Pass
          </button>
        </div>

        <div class="table-container">
          <table class="passes-table">
            <thead>
              <tr>
                <th>Pass #</th>
                <th>Speed</th>
                <th>Rope Length</th>
                <th>Buoys</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(pass, index) in passes" :key="pass.id || `new-${index}`">
                <td>
                  <input
                    type="number"
                    v-model="pass.pass_number"
                    @blur="savePass(pass, index)"
                    class="table-input small"
                    min="1"
                  />
                </td>
                <td>
                  <select
                    v-model="pass.boat_speed"
                    @change="savePass(pass, index)"
                    class="table-input"
                  >
                    <option value="">Select...</option>
                    <option v-for="speed in sortedSpeedChoices" :key="speed.value" :value="speed.value">
                      {{ speed.label }}
                    </option>
                  </select>
                </td>
                <td>
                  <select
                    v-model="pass.rope_length"
                    @change="savePass(pass, index)"
                    class="table-input"
                  >
                    <option value="">Select...</option>
                    <option v-for="(label, value) in ropeChoices" :key="value" :value="value">
                      {{ label }}
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    v-model="pass.buoys"
                    @blur="savePass(pass, index)"
                    class="table-input small"
                    min="0"
                    max="6"
                  />
                </td>
                <td>
                  <button
                    @click="deletePass(pass, index)"
                    class="delete-btn"
                    title="Delete pass"
                  >
                    ×
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Ski Settings Section -->
      <div class="ski-settings-section">
        <div class="section-header">
          <h3>Ski Settings</h3>
          <div class="settings-info">
            <span class="settings-label">{{ log.ski_settings_snapshot ? 'Settings from log date' : 'Current settings (no snapshot available)' }}</span>
          </div>
        </div>

        <div v-if="loadingSkiSettings" class="loading-message">
          Loading ski settings...
        </div>
        
        <div v-else class="settings-grid">
          <div class="form-group">
            <label for="boot_placement">Boot Placement</label>
            <input
              type="text"
              id="boot_placement"
              v-model="skiSettings.boot_placement"
              placeholder="e.g., Forward 2cm"
              class="form-input"
readonly
            />
          </div>

          <div class="form-group">
            <label for="fin_length">Fin Length</label>
            <input
              type="text"
              id="fin_length"
              v-model="skiSettings.fin_length"
              placeholder="e.g., 6.5 inches"
              class="form-input"
readonly
            />
          </div>

          <div class="form-group">
            <label for="fin_depth">Fin Depth</label>
            <input
              type="text"
              id="fin_depth"
              v-model="skiSettings.fin_depth"
              placeholder="e.g., 1.2 inches"
              class="form-input"
readonly
            />
          </div>

          <div class="form-group">
            <label for="fin_dft">Fin DFT</label>
            <input
              type="text"
              id="fin_dft"
              v-model="skiSettings.fin_dft"
              placeholder="e.g., 0.8 inches"
              class="form-input"
readonly
            />
          </div>

          <div class="form-group">
            <label for="wing_degree">Wing Degree</label>
            <input
              type="text"
              id="wing_degree"
              v-model="skiSettings.wing_degree"
              placeholder="e.g., 2 degrees"
              class="form-input"
readonly
            />
          </div>
        </div>
      </div>

      <!-- Feedback Section -->
      <div class="feedback-section">
        <h3>Session Feedback</h3>
        <div class="feedback-grid">
          <div class="form-group">
            <label for="positive">What went well?</label>
            <textarea
              id="positive"
              v-model="log.positive"
              placeholder="Things that went well during this session..."
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>

          <div class="form-group">
            <label for="negative">What didn't go well?</label>
            <textarea
              id="negative"
              v-model="log.negative"
              placeholder="Things that could have gone better..."
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>

          <div class="form-group">
            <label for="improve">What to improve?</label>
            <textarea
              id="improve"
              v-model="log.improve"
              placeholder="Areas to focus on improving..."
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>

          <div class="form-group">
            <label for="try_next">What to try next time?</label>
            <textarea
              id="try_next"
              v-model="log.try_next"
              placeholder="Things to try in the next session..."
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import axios from 'axios';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

export default {
  name: 'SkiLogDetail',
  components: {
    LoadingSpinner
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      log: {
        date: new Date().toISOString().split('T')[0],
        lake: '',
        boat: '',
        boat_type: '',
        ski: '',
        ski_type: '',
        driver: '',
        conditions: '',
        conditions_detail: '',
        positive: '',
        negative: '',
        improve: '',
        try_next: ''
      },
      passes: [],
      skiSettings: {
        boot_placement: '',
        fin_length: '',
        fin_depth: '',
        fin_dft: '',
        wing_degree: ''
      },
      loading: true,
      saving: false,
      loadingSkiSettings: false,
      boatChoices: {
        'nautique': 'Nautique',
        'mastercraft': 'MasterCraft',
        'malibu': 'Malibu',
        'centurion': 'Centurion',
        'other': 'Other',
      },
      skiChoices: {
        'connelly': 'Connelly',
        'd3': 'D3',
        'goode': 'Goode',
        'ho': 'HO',
        'kd': 'KD',
        'radar': 'Radar',
        'reflex': 'Reflex',
        'obrien': 'Obrien',
        'denali': 'Denali',
        'pineapple': 'Pineapple',
        'lapoint': 'Lapoint',
        'other': 'Other',
      },
      speedChoices: {
        '58': '58kph - 36mph',
        '57': '57kph - 35mph',
        '56': '56kph - 35mph',
        '55': '55kph - 34mph',
        '54': '54kph - 34mph',
        '53': '53kph - 33mph',
        '52': '52kph - 32mph',
        '51': '51kph - 32mph',
        '50': '50kph - 31mph',
        '49': '49kph - 30mph',
        '48': '48kph - 30mph',
        '47': '47kph - 29mph',
        '46': '46kph - 28mph',
        '45': '45kph - 28mph',
        '44': '44kph - 27mph',
        '43': '43kph - 26mph',
        '42': '42kph - 26mph',
        '41': '41kph - 25mph',
        '40': '40kph - 25mph',
        '39': '39kph - 24mph',
        '38': '38kph - 24mph',
        '37': '37kph - 23mph',
      },
      ropeChoices: {
        '18.25': '18.25 meter - 15 Off',
        '16.00': '16.00 meter - 22 Off',
        '14.25': '14.25 meter - 28 Off',
        '13.00': '13.00 meter - 32 Off',
        '12.00': '12.00 meter - 35 Off',
        '11.25': '11.25 meter - 38 Off',
        '10.75': '10.75 meter - 39.5 Off',
        '10.25': '10.25 meter - 41 Off',
        '9.75': '9.75 meter - 43 Off',
      }
    };
  },
  computed: {
    sortedSpeedChoices() {
      // Convert object to array and sort by speed (descending - fast to slow)
      return Object.entries(this.speedChoices)
        .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
        .map(([value, label]) => ({ value, label }));
    }
  },
  async mounted() {
    await this.fetchLogDetail();
    await this.fetchPasses();
    // Fetch ski settings after log detail is loaded (need log data for snapshot)
    await this.fetchSkiSettings();
  },
  methods: {
    async fetchLogDetail() {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/skilog/logs/${this.id}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.log = { ...this.log, ...response.data };
      } catch (error) {
        console.error('Error fetching log detail:', error);
        this.$router.push({ name: 'SkiLog' });
      }
    },

    async fetchPasses() {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/skilog/logs/${this.id}/passes/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.passes = response.data;
      } catch (error) {
        console.error('Error fetching passes:', error);
      } finally {
        this.loading = false;
      }
    },

    async saveLog() {
      this.saving = true;
      try {
        const token = localStorage.getItem('access_token');
        await axios.patch(`${API_BASE_URL}/skilog/logs/${this.id}/update/`, this.log, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error('Error saving log:', error);
        alert('Error saving log. Please try again.');
      } finally {
        this.saving = false;
      }
    },

    async deleteLog() {
      if (!confirm('Are you sure you want to delete this log? This action cannot be undone.')) {
        return;
      }

      try {
        const token = localStorage.getItem('access_token');
        await axios.delete(`${API_BASE_URL}/skilog/logs/${this.id}/delete/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.$router.push({ name: 'SkiLog' });
      } catch (error) {
        console.error('Error deleting log:', error);
        alert('Error deleting log. Please try again.');
      }
    },

    addPass() {
      const newPass = {
        pass_number: this.passes.length + 1,
        boat_speed: '',
        rope_length: '',
        buoys: '',
        isNew: true
      };
      this.passes.push(newPass);
    },

    async savePass(pass, index) {
      
      try {
        const token = localStorage.getItem('access_token');
        
        if (pass.isNew) {
          // Only create if pass has meaningful data
          const hasData = pass.pass_number || pass.boat_speed || pass.rope_length || pass.buoys;
          if (!hasData) {
            return; // Don't create empty passes
          }
          
          // Format the data properly like in create flow
          const passData = {
            log: this.id,
            pass_number: pass.pass_number || null,
            boat_speed: pass.boat_speed || null,
            rope_length: pass.rope_length ? parseFloat(pass.rope_length) : null,
            buoys: pass.buoys || null
          };
          
          // Create new pass
          const response = await axios.post(`${API_BASE_URL}/skilog/logs/${this.id}/passes/create/`, passData, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          // Update the pass with the response data
          this.passes[index] = response.data;
        } else if (pass.id) {
          // Update existing pass - format data properly
          const passData = {
            pass_number: pass.pass_number || null,
            boat_speed: pass.boat_speed || null,
            rope_length: pass.rope_length ? parseFloat(pass.rope_length) : null,
            buoys: pass.buoys || null
          };
          
          await axios.patch(`${API_BASE_URL}/skilog/logs/${this.id}/passes/${pass.id}/update/`, passData, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
        }
      } catch (error) {
        console.error('Error saving pass:', error);
      }
    },

    async deletePass(pass, index) {
      
      try {
        const token = localStorage.getItem('access_token');
        
        if (pass.id) {
          await axios.delete(`${API_BASE_URL}/skilog/logs/${this.id}/passes/${pass.id}/delete/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
        }
        
        this.passes.splice(index, 1);
      } catch (error) {
        console.error('Error deleting pass:', error);
      }
    },

    async fetchSkiSettings() {
      this.loadingSkiSettings = true;
      try {
        // For existing logs, use the snapshot from the log if it exists
        if (this.log.ski_settings_snapshot) {
          this.skiSettings = {
            boot_placement: this.log.ski_settings_snapshot.boot_placement || '',
            fin_length: this.log.ski_settings_snapshot.fin_length || '',
            fin_depth: this.log.ski_settings_snapshot.fin_depth || '',
            fin_dft: this.log.ski_settings_snapshot.fin_dft || '',
            wing_degree: this.log.ski_settings_snapshot.wing_degree || ''
          };
        } else {
          // Fallback to current settings for logs without snapshots (legacy logs)
          const token = localStorage.getItem('access_token');
          const response = await axios.get(`${API_BASE_URL}/api/ski-settings/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.skiSettings = {
            boot_placement: response.data.boot_placement || '',
            fin_length: response.data.fin_length || '',
            fin_depth: response.data.fin_depth || '',
            fin_dft: response.data.fin_dft || '',
            wing_degree: response.data.wing_degree || ''
          };
        }
      } catch (error) {
        console.error('Error fetching ski settings:', error);
        // If settings don't exist, that's okay
      } finally {
        this.loadingSkiSettings = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'SkiLog' });
    }
  }
};
</script>

<style scoped>
.ski-log-detail-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  background-color: #15202b;
  min-height: 100vh;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.back-button {
  background-color: #8899a6;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}

.back-button:hover {
  background-color: #7a8a97;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.log-form-card, .passes-section, .ski-settings-section, .feedback-section {
  background-color: #192734;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
}

.log-form-card h2 {
  color: #ffffff;
  margin: 0 0 24px 0;
  font-size: 1.5rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.feedback-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.settings-actions {
  display: flex;
  gap: 12px;
}

.settings-info {
  display: flex;
  align-items: center;
}

.settings-label {
  color: #8899a6;
  font-size: 0.9em;
  font-style: italic;
}

.loading-message {
  color: #8899a6;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  color: #ffffff;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-input, .form-textarea {
  padding: 12px 16px;
  border: 1px solid #38444d;
  border-radius: 8px;
  background-color: #15202b;
  color: #ffffff;
  font-size: 16px;
  box-sizing: border-box;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: #1da1f2;
}

.form-input[readonly] {
  background-color: #0f1419;
  color: #8899a6;
  cursor: default;
}

.form-textarea {
  resize: vertical;
  font-family: inherit;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  color: #ffffff;
  margin: 0;
  font-size: 1.3rem;
}

.table-container {
  overflow-x: auto;
}

.passes-table {
  width: 100%;
  border-collapse: collapse;
  background-color: #15202b;
  border-radius: 8px;
  overflow: hidden;
}

.passes-table th,
.passes-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #38444d;
}

.passes-table th {
  background-color: #22303c;
  color: #ffffff;
  font-weight: 600;
}

.passes-table td {
  color: #ffffff;
}

.table-input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #38444d;
  border-radius: 4px;
  background-color: #15202b;
  color: #ffffff;
  font-size: 14px;
}

.table-input.small {
  width: 80px;
}

.table-input:focus {
  outline: none;
  border-color: #1da1f2;
}

.delete-btn {
  background-color: #e1306c;
  color: white;
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-btn:hover {
  background-color: #c92a5d;
}

.action-button {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-button.primary {
  background-color: #1da1f2;
  color: white;
}

.action-button.primary:hover {
  background-color: #1991db;
}

.action-button.primary:disabled {
  background-color: #38444d;
  color: #657786;
  cursor: not-allowed;
}

.action-button.secondary {
  background-color: #38444d;
  color: #ffffff;
}

.action-button.secondary:hover {
  background-color: #4a5a67;
}

.action-button.danger {
  background-color: #e1306c;
  color: white;
}

.action-button.danger:hover {
  background-color: #c92a5d;
}

.action-button.small {
  padding: 8px 16px;
  font-size: 14px;
}

.feedback-section h3 {
  color: #ffffff;
  margin: 0 0 20px 0;
  font-size: 1.3rem;
}

@media screen and (max-width: 768px) {
  .ski-log-detail-container {
    padding: 16px;
  }
  
  .header {
    flex-direction: column;
    gap: 16px;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .feedback-grid {
    grid-template-columns: 1fr;
  }
  
  .settings-grid {
    grid-template-columns: 1fr;
  }
  
  .table-container {
    font-size: 14px;
  }
  
  .passes-table th,
  .passes-table td {
    padding: 8px 4px;
  }
}
</style>
